// TRAVA DE IDENTIFICACAO DO PEDIDO — as tres saidas do Enviar ao Caixa ([DA-697], 09/09/2026).
//
// Pedido dele: metade das vendas ia ao caixa sem ninguem saber quem comprou (medido em 30 dias:
// 477 de 818, ~17,7 por dia, ticket R$ 61,20). O envio passa a exigir UMA das tres:
//
//   1. cliente do cadastro **com telefone na ficha**;
//   2. um telefone capturado no pedido (vira contato da inbox Atendimento);
//   3. a recusa registrada ("nao quis informar", migration 0277).
//
// ⛔ **Peca UNICA de proposito.** A regra vale no servidor (quem nega de verdade) e na tela (que
// nao pode oferecer o que o servidor vai negar). Escrita duas vezes, seriam dois lugares pra ela
// divergir — e o modo de falhar e' silencioso nos dois sentidos: tela que trava o que o servidor
// aceitaria, ou tela que libera o que ele recusa, e ai a vendedora leva um erro no rosto com o
// cliente na frente do balcao.
//
// ⚠️ **A saida 1 exige numero, nao so' cliente escolhido**: 17 vendas em 30 dias foram pra fichas
// sem telefone nenhum, e 83 dos 297 clientes estao assim. Sem essa condicao, "escolher o cliente"
// satisfaria a trava sem capturar contato algum — foi o caso do ALMIR, 9 compras no mes e a ficha
// muda. Decisao dele em 09/09/2026: *"Cadastro sem numero algum, deve ser informado na hora"*.

/** O CLIENTE do balcao anonimo. O cadastro 1 e' "Consumidor" — presenca dele nao identifica ninguem. */
export const CLIENTE_CONSUMIDOR_ID = 1;

/**
 * O numero da ficha pode receber WhatsApp? ([DA-698], achado por ele em 09/09/2026.)
 *
 * ⛔ **Ate hoje a trava aceitava "whatsapp OU telefone", e telefone FIXO nao recebe fly nenhum.**
 * O caso que abriu isto foi a ficha `3RN CAPITAL`: WhatsApp vazio, `telefone = 6233753529` (fixo de
 * Goiania) — o pedido ia direto pro pagamento sem capturar nada, que e' exatamente o buraco que a
 * trava existe pra fechar.
 *
 * ⚠️ Medido antes de apertar: das 41 fichas ativas com telefone e sem WhatsApp, **36 sao fixo** e
 * 5 tem celular gravado no campo errado — e juntas elas fizeram **2 vendas em 30 dias**. Por isso
 * a regra le o FORMATO em vez de exigir o campo `whatsapp`: nao cobra numero de quem ja tem
 * celular na ficha, e cobra de quem so' tem fixo.
 *
 * ⛔ **NAO basta exigir os 11 digitos do celular novo** — e essa foi a primeira versao desta
 * funcao, errada, achada horas depois pela lista de conversas recentes: **468 dos 481 contatos que
 * ja falaram com a loja tem o numero SEM o 9o digito** (10 digitos + DDI), porque e' assim que o
 * WhatsApp guarda a linha antiga, e 299 deles falaram nos ultimos 90 dias. Pela regra dos 11
 * digitos, **67 fichas de cliente com WhatsApp que funciona** seriam cobradas em toda venda.
 *
 * Entao a conta separa CELULAR de FIXO pelo primeiro digito do numero (depois do DDD):
 *   - 11 digitos → celular novo, o `9` na frente;
 *   - 10 digitos → celular antigo se comeca em **6-9**; **2-5 e' fixo** e nao recebe nada.
 * ⚠️ O DDI so' e' descartado quando sobra numero pra isso — senao o **DDD 55** (Rio Grande do Sul)
 * seria comido, e um celular gaucho legitimo viraria "fixo".
 */
export function numeroPodeSerWhatsapp(valor?: string | null): boolean {
  let d = String(valor ?? '').replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  if (d.length === 11) return d[2] === '9';
  if (d.length === 10) return /[6-9]/.test(d[2] ?? '');
  return false;
}

export interface EstadoIdentificacao {
  /** `cliente_id` do pedido. `1`/nulo = Consumidor, ou seja ninguem. */
  clienteId?: number | null;
  /**
   * A ficha do cliente tem um numero que RECEBE WhatsApp (`numeroPodeSerWhatsapp`)?
   *
   * ⚠️ `undefined`/`null` significa **"ainda nao perguntei ao cadastro"**, e nesse estado a trava
   * fica DE PE. Tratar desconhecido como "tem" liberaria justamente a ficha vazia, que e' o caso
   * que esta regra existe pra pegar ([DA-649] pelo lado da UI: o sinal so' acende depois que o
   * estado chega).
   */
  clienteTemNumero?: boolean | null;
  /** Contato capturado neste pedido (migration 0281). */
  contatoCapturadoId?: number | null;
  /** "Nao quis informar o Telefone" (migration 0277). */
  telefoneRecusado?: boolean | null;
  /**
   * O nome que o cliente disse, na recusa (migration 0307, [DA-780]).
   *
   * ⛔ **A recusa so' vale com NOME e ETIQUETA** — decisao dele em 03/10/2026 (*"os dois campos
   * obrigatorios"*). Sem eles o placar "Nao quis informar" listava 14 de 14 vendas como
   * "Consumidor", e nao havia como avaliar POR QUE o cliente recusou. A marca sozinha (que a
   * caixinha do cabecalho do orcamento ainda grava) deixa a trava de pe.
   */
  recusaNome?: string | null;
  /** Quantas etiquetas a recusa carrega (`recusa_etiqueta_ids`). */
  recusaEtiquetas?: number | null;
}

/** A recusa esta completa: marcada, com nome (2+ letras) e ao menos uma etiqueta ([DA-780]). */
export function recusaCompleta(e: Pick<EstadoIdentificacao, 'telefoneRecusado' | 'recusaNome' | 'recusaEtiquetas'>): boolean {
  return e.telefoneRecusado === true
    && String(e.recusaNome ?? '').trim().length >= 2
    && Number(e.recusaEtiquetas ?? 0) > 0;
}

export type MotivoIdentificacao =
  | 'cliente_com_numero'
  | 'cliente_cadastrado'
  | 'contato_capturado'
  | 'recusa_registrada';

/**
 * Qual das tres saidas esta satisfeita, ou `null` se nenhuma.
 *
 * ⛔ **Cliente do cadastro libera SEMPRE, com ou sem WhatsApp na ficha** ([DA-867], decisao dele em
 * 30/10/2026, revogando a exigencia de numero de 09/09). O caso que derrubou: a REDE SUPER TRIUNFO
 * (so' fixo na ficha) travou no balcao e o cliente teve que ligar pra empresa pra pegar o telefone
 * do financeiro. *"Quando nao ha WhatsApp, o sistema nao bloqueia em ponto algum; deixa o usuario
 * informar, mas so' opcional."* O `clienteTemNumero` segue no estado so' pra distinguir o motivo
 * (a tela usa pra oferecer o campo opcional) — nao decide mais nada.
 */
export function saidaIdentificacao(e: EstadoIdentificacao): MotivoIdentificacao | null {
  const temCliente = e.clienteId != null && Number(e.clienteId) !== CLIENTE_CONSUMIDOR_ID;
  if (temCliente) return e.clienteTemNumero === true ? 'cliente_com_numero' : 'cliente_cadastrado';
  if (e.contatoCapturadoId != null) return 'contato_capturado';
  if (recusaCompleta(e)) return 'recusa_registrada';
  return null;
}

/** O pedido pode ir ao caixa? */
export function pedidoIdentificado(e: EstadoIdentificacao): boolean {
  return saidaIdentificacao(e) !== null;
}

/**
 * O texto que o servidor devolve e a tela mostra. Nomeia as TRES saidas de propostio: recusa que
 * so' diz "faltou o cliente" manda a vendedora procurar um caminho que ela talvez nao tenha (o
 * cliente pode nao querer se identificar), e ai a venda fica presa.
 */
export const MSG_FALTA_IDENTIFICAR =
  'Informe quem esta comprando antes de enviar ao caixa: escolha o cliente, cadastre o WhatsApp, ou registre que o cliente nao quis informar (com o nome e a etiqueta dele).';
